import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatCPF, onlyDigits } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Power, Trash2, ShieldCheck, Pencil, AlertTriangle, Loader2 } from "lucide-react";

const ROLE_LABELS = { super_admin: "Super Admin", funcionario: "Funcionário", cidadao: "Cidadão" };
const ROLE_BADGE_STYLES = {
  super_admin: "bg-[#083CA6] text-white",
  funcionario: "bg-[#00883A] text-white",
  cidadao: "bg-slate-200",
};
const roleLabel = (role) => ROLE_LABELS[role] || role;
const roleBadgeStyle = (role) => ROLE_BADGE_STYLES[role] || "bg-slate-200";

function PermissoesDialog({ open, onOpenChange, targetUser, disponiveis, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && targetUser) {
      const p = targetUser.permissoes;
      // null/undefined = padrão do sistema
      setSelected(p == null ? [...(disponiveis?.default || [])] : [...p]);
    }
  }, [open, targetUser, disponiveis]);  // setSelected é estável (setter React)

  const toggle = (perm) => {
    setSelected((prev) => prev.includes(perm) ? prev.filter((x) => x !== perm) : [...prev, perm]);
  };

  const selecionarPadrao = () => setSelected([...(disponiveis?.default || [])]);
  const selecionarTodas = () => setSelected([...(disponiveis?.permissoes || [])]);
  const selecionarNenhuma = () => setSelected([]);

  const salvar = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${targetUser.id}/permissoes`, { permissoes: selected });
      toast.success("Permissões atualizadas");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      logger.error("Falha ao salvar permissões", e);
      toast.error(e.response?.data?.detail || "Erro ao salvar permissões");
    } finally { setSaving(false); }
  };

  if (!targetUser) return null;
  const labels = disponiveis?.labels || {};
  const groups = disponiveis?.groups || [];
  const totalPerms = disponiveis?.permissoes?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="permissoes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#083CA6]" />
            Permissões do Funcionário
          </DialogTitle>
          <DialogDescription>
            {targetUser.nome} — {formatCPF(targetUser.cpf)}. Marque as ações que este funcionário poderá realizar no painel.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 py-2 border-y">
          <span className="text-xs text-slate-500 self-center mr-1">Atalhos:</span>
          <Button size="sm" variant="outline" onClick={selecionarPadrao} type="button" data-testid="btn-perms-padrao">Padrão</Button>
          <Button size="sm" variant="outline" onClick={selecionarTodas} type="button" data-testid="btn-perms-todas">Todas</Button>
          <Button size="sm" variant="outline" onClick={selecionarNenhuma} type="button" data-testid="btn-perms-nenhuma">Nenhuma</Button>
          <span className="text-xs text-slate-500 self-center ml-auto">{selected.length} / {totalPerms} marcadas</span>
        </div>

        <div className="space-y-4 py-2">
          {groups.map((grp) => (
            <div key={grp.label}>
              <h4 className="text-sm font-bold text-[#083CA6] mb-2">{grp.label}</h4>
              <div className="space-y-2">
                {grp.permissoes.map((perm) => (
                  <label key={perm} className="flex items-start gap-3 p-3 rounded border border-slate-200 hover:border-[#083CA6] cursor-pointer transition-colors">
                    <Checkbox
                      checked={selected.includes(perm)}
                      onCheckedChange={() => toggle(perm)}
                      data-testid={`chk-perm-${perm}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{perm}</div>
                      <div className="text-xs text-slate-500">{labels[perm] || ""}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving}
                  className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-salvar-permissoes">
            {saving ? "Salvando..." : "Salvar Permissões"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ open, onOpenChange, targetUser, onSaved }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("funcionario");
  const [newPassword, setNewPassword] = useState("");
  const [receberDigest, setReceberDigest] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && targetUser) {
      setNome(targetUser.nome || "");
      setEmail(targetUser.email || "");
      setRole(targetUser.role || "funcionario");
      setNewPassword("");
      setReceberDigest(targetUser.receber_pendencias_digest !== false);
    }
  }, [open, targetUser]);

  const salvar = async () => {
    if (!targetUser) return;
    setSaving(true);
    try {
      const body = {};
      if (nome !== targetUser.nome) body.nome = nome;
      if ((email || null) !== (targetUser.email || null)) body.email = email;
      if (role !== targetUser.role) body.role = role;
      if (newPassword) body.new_password = newPassword;
      const currentDigest = targetUser.receber_pendencias_digest !== false;
      if (receberDigest !== currentDigest) body.receber_pendencias_digest = receberDigest;
      if (Object.keys(body).length === 0) {
        toast.info("Nada para atualizar"); setSaving(false); return;
      }
      await api.put(`/users/${targetUser.id}`, body);
      toast.success("Usuário atualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      logger.error("Falha ao atualizar usuário", e);
      toast.error(e.response?.data?.detail || "Erro ao atualizar");
    } finally { setSaving(false); }
  };

  if (!targetUser) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[#083CA6]" />
            Editar Usuário
          </DialogTitle>
          <DialogDescription>
            {formatCPF(targetUser.cpf)} — CPF é imutável. Para alterar o CPF, exclua e recadastre.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Nome</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="edit-user-nome" />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-user-email" />
          </div>
          <div>
            <Label>Perfil</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="funcionario">Funcionário</SelectItem>
                <SelectItem value="cidadao">Cidadão</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nova Senha (opcional)</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Deixe em branco para manter a atual"
                    minLength={6} data-testid="edit-user-senha" />
          </div>
          {role !== "cidadao" && (
            <div className="flex items-start gap-2 p-2 rounded bg-slate-50 border">
              <input
                type="checkbox"
                checked={receberDigest}
                onChange={(e) => setReceberDigest(e.target.checked)}
                id="chk-receber-digest"
                className="mt-1"
                data-testid="edit-user-receber-digest"
              />
              <label htmlFor="chk-receber-digest" className="text-sm text-slate-700 cursor-pointer">
                <strong>Receber e-mail diário de pendências</strong>
                <div className="text-xs text-slate-500">
                  Resumo matinal com inscrições que precisam de atenção (requer e-mail cadastrado).
                </div>
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving}
                  className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-salvar-user-edit">
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function UsuariosPage() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ cpf: "", nome: "", email: "", password: "", role: "funcionario" });
  const [disponiveis, setDisponiveis] = useState({ permissoes: [], labels: {}, groups: [], default: [] });
  const [dialogTarget, setDialogTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/users"); setUsers(r.data); }
    catch (e) { logger.error("Falha ao listar usuários", e); }
  }, []);

  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/users/permissoes/disponiveis")
      .then((r) => setDisponiveis(r.data))
      .catch((e) => logger.error("Falha ao carregar permissões disponíveis", e));
  }, [load]);

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => {
    const selectables = users.filter((u) => u.id !== me?.id);
    if (selected.size === selectables.length && selectables.length > 0) setSelected(new Set());
    else setSelected(new Set(selectables.map((u) => u.id)));
  };

  const confirmBulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/users/bulk-delete", { ids });
      toast.success(`${r.data.removidos} usuário(s) excluído(s)`);
      setSelected(new Set());
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir em lote");
    } finally { setBulkDeleting(false); }
  };

  const criar = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, cpf: onlyDigits(form.cpf) });
      toast.success("Usuário criado");
      setForm({ cpf: "", nome: "", email: "", password: "", role: "funcionario" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toggle = async (id) => {
    try { await api.put(`/users/${id}/toggle`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir este usuário?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const totalPerms = disponiveis.permissoes?.length || 0;
  const defaultPermsCount = disponiveis.default?.length || 0;
  const permsCount = (u) => {
    if (u.role !== "funcionario") return null;
    const p = u.permissoes;
    return p == null ? defaultPermsCount : p.length;
  };

  // Preview para o dialog de exclusão em lote. Memoizado para evitar
  // filter+slice a cada render enquanto o dialog permanece aberto.
  const bulkPreview = useMemo(
    () => users.filter((u) => selected.has(u.id)).slice(0, 20),
    [users, selected]
  );

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6]">Usuários</h1>
        <p className="text-sm text-slate-600">Super Admins, Funcionários e Cidadãos. Funcionários podem ter permissões por aba.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><UserPlus className="w-4 h-4" />Novo Usuário</CardTitle>
          <CardDescription>Cadastre administradores ou funcionários adicionais.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={criar} className="grid md:grid-cols-5 gap-3">
            <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} required data-testid="user-nome" /></div>
            <div><Label>CPF</Label><Input value={form.cpf} onChange={(e) => setForm({...form, cpf: formatCPF(e.target.value)})} required data-testid="user-cpf" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email" /></div>
            <div><Label>Senha</Label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6} data-testid="user-senha" /></div>
            <div>
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="funcionario">Funcionário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-5"><Button type="submit" className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-criar-user">Criar Usuário</Button></div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {selected.size > 0 && (
            <div className="bg-red-50 border-b border-red-200 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="user-bulk-bar">
              <div className="text-sm">
                <strong>{selected.size}</strong> {selected.size === 1 ? "usuário selecionado" : "usuários selecionados"}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="btn-user-bulk-clear">
                  Limpar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setBulkOpen(true)} data-testid="btn-user-bulk-delete">
                  <Trash2 className="w-4 h-4 mr-2" /> Excluir {selected.size} selecionados
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-3 w-10">
                  <Checkbox
                    checked={users.length > 0 && selected.size > 0 && selected.size === users.filter((u) => u.id !== me?.id).length}
                    onCheckedChange={toggleAll}
                    aria-label="Selecionar todos"
                    data-testid="chk-user-select-all"
                  />
                </th>
                <th className="p-3">Nome</th><th className="p-3">CPF</th><th className="p-3">E-mail</th>
                <th className="p-3">Perfil</th><th className="p-3">Permissões</th><th className="p-3">Status</th><th className="p-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`user-row-${u.id}`}>
                  <td className="p-3">
                    {u.id !== me?.id && (
                      <Checkbox
                        checked={selected.has(u.id)}
                        onCheckedChange={() => toggleOne(u.id)}
                        aria-label={`Selecionar ${u.nome}`}
                        data-testid={`chk-user-${u.id}`}
                      />
                    )}
                  </td>
                  <td className="p-3 font-medium">{u.nome}{u.id === me?.id && <span className="ml-1 text-xs text-slate-400">(você)</span>}</td>
                  <td className="p-3">{formatCPF(u.cpf)}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">
                    <Badge className={roleBadgeStyle(u.role)}>{roleLabel(u.role)}</Badge>
                  </td>
                  <td className="p-3 text-xs text-slate-600">
                    {u.role === "super_admin" && <span className="text-slate-500 italic">Todas</span>}
                    {u.role === "funcionario" && (
                      <span>{permsCount(u)}/{totalPerms}{u.permissoes == null && " (padrão)"}</span>
                    )}
                    {u.role === "cidadao" && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3"><Badge className={u.ativo ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>{u.ativo ? "Ativo" : "Inativo"}</Badge></td>
                  <td className="p-3 flex gap-2">
                    <Button size="sm" variant="outline" title="Editar usuário"
                            onClick={() => setEditTarget(u)} data-testid={`btn-edit-${u.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {u.role === "funcionario" && (
                      <Button size="sm" variant="outline" title="Editar permissões" onClick={() => setDialogTarget(u)} data-testid={`btn-perms-${u.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" title={u.ativo ? "Desativar" : "Ativar"}
                            onClick={() => toggle(u.id)}>
                      <Power className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" title="Excluir"
                            onClick={() => excluir(u.id)} disabled={u.id === me?.id}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <PermissoesDialog
        open={!!dialogTarget}
        onOpenChange={(v) => { if (!v) setDialogTarget(null); }}
        targetUser={dialogTarget}
        disponiveis={disponiveis}
        onSaved={load}
      />

      <EditUserDialog
        open={!!editTarget}
        onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        targetUser={editTarget}
        onSaved={load}
      />

      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="max-w-md" data-testid="dialog-user-bulk-delete">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Excluir {selected.size} Usuários
            </DialogTitle>
            <DialogDescription>
              Esta ação é permanente. Usuários excluídos perdem acesso ao sistema imediatamente.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs max-h-40 overflow-y-auto">
            {bulkPreview.map((u) => (
              <div key={u.id} className="truncate">
                <strong>{u.nome}</strong> — {formatCPF(u.cpf)} <span className="text-slate-500">({roleLabel(u.role)})</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkDeleting}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmBulkDelete} disabled={bulkDeleting}
                    data-testid="btn-confirm-user-bulk-delete">
              {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {bulkDeleting ? "Excluindo..." : `Excluir ${selected.size} usuários`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
